import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, X, Send, Bot, User, Calendar, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface KnowledgeItem {
  id: string;
  pergunta: string;
  palavras_chave: string[];
  resposta: string;
  categoria: string;
}

interface Contact {
  nome: string;
  cargo?: string;
  setor?: string;
  email?: string;
  telefone?: string;
  ramal?: string | number;
  localizacao?: string;
}

interface MenuItem {
  data: string; // dd/MM/yyyy
  prato: string;
  proteina: string;
  acompanhamentos?: string[];
  sobremesa?: string;
}

interface Aniversariante {
  nome: string;
  dataNascimento: string;
  dia: number;
  mesNumero: number;
}

export const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Olá! 👋 Sou o assistente da Cropfield.\n\n**Faça perguntas específicas:**\n\n📞 "Qual o ramal de [nome completo]?"\n🏢 "Quem trabalha no RH?"\n📍 "Contatos de Apucarana"\n🍽️ "Cardápio de hoje"\n📅 "Como reservo uma sala?"\n🎂 "Aniversariantes de hoje"\n\n💡 Use nomes completos para melhor precisão!',
      isBot: true,
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeItem[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [cardapio, setCardapio] = useState<MenuItem[]>([]);
  const [aniversariantes, setAniversariantes] = useState<Aniversariante[]>([]);
  const [loading, setLoading] = useState(false);

  // Carregar todas as bases de dados da empresa
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('[CHATBOT] Loading knowledge base and company data...');

        if (!supabase) {
          console.error('[CHATBOT] ❌ Supabase client not initialized!');
          return;
        }
        
        // Base de conhecimento
        const knowledgeResponse = await fetch('/dados/chatbot_knowledge.json');
        if (knowledgeResponse.ok) {
          const knowledgeData = await knowledgeResponse.json();
          setKnowledgeBase(Array.isArray(knowledgeData) ? knowledgeData : []);
          console.log('[CHATBOT] Knowledge base loaded:', knowledgeData?.length, 'items');
        }

        // Contatos - buscar APENAS do Supabase (fonte única de verdade)
        const { data: contactsData, error: contactsError } = await supabase
          .from('contatos')
          .select('*')
          .eq('ativo', true);

        if (!contactsError && contactsData && contactsData.length > 0) {
          // Ordenar: equipe primeiro, representantes por último
          const sortedData = contactsData.sort((a, b) => {
            // Primeiro por tipo: equipe antes de representante
            if (a.tipo !== b.tipo) {
              if (a.tipo === 'equipe') return -1;
              if (b.tipo === 'equipe') return 1;
              if (a.tipo === 'representante') return 1;
              if (b.tipo === 'representante') return -1;
            }
            // Depois por nome
            return a.nome.localeCompare(b.nome, 'pt-BR');
          });

          const allContacts: Contact[] = sortedData.map(c => ({
            nome: c.nome,
            cargo: c.cargo,
            setor: c.setor,
            email: c.email,
            telefone: c.telefone,
            ramal: c.ramal,
            localizacao: c.localizacao
          }));
          setContacts(allContacts);
          console.log('[CHATBOT] ✅ Contacts loaded from Supabase database:', allContacts.length, 'contacts');
          console.log('[CHATBOT] ✅ Contacts with ramais:', allContacts.filter(c => c.ramal).length);

          // Teste específico: buscar Felipe Schneider
          const felipe = allContacts.find(c => c.nome.toLowerCase().includes('felipe schneider'));
          if (felipe) {
            console.log('[CHATBOT] 📞 Felipe Schneider ramal:', felipe.ramal);
          }
        } else {
          console.error('[CHATBOT] ❌ Error loading contacts from database:', contactsError);
          console.error('[CHATBOT] ❌ Chatbot will NOT work without database contacts!');
          setContacts([]);
        }

        // Cardápio
        const cardapioResponse = await fetch('/cardapio/cardapio-agosto-padrao.json');
        if (cardapioResponse.ok) {
          const cardapioData = await cardapioResponse.json();

          // Transformar dados do cardápio padrão para o formato esperado
          const menuItems: MenuItem[] = Array.isArray(cardapioData)
            ? cardapioData.map((item: any) => ({
                data: item.data,
                prato: Array.isArray(item.proteina) ? item.proteina.join(' + ') : item.proteina || 'Cardápio do dia',
                proteina: Array.isArray(item.proteina) ? item.proteina[0] : item.proteina || '',
                acompanhamentos: item.acompanhamentos || [],
                sobremesa: item.sobremesa || ''
              }))
            : [];

          setCardapio(menuItems);
          console.log('[CHATBOT] Menu loaded:', menuItems.length, 'days');
        }

        // Aniversariantes
        const aniversariantesResponse = await fetch('/dados/aniversariantes.json');
        if (aniversariantesResponse.ok) {
          const aniversariantesData = await aniversariantesResponse.json();
          setAniversariantes(Array.isArray(aniversariantesData) ? aniversariantesData : []);
          console.log('[CHATBOT] Birthdays loaded:', aniversariantesData?.length, 'people');
        }

      } catch (error) {
        console.error('[CHATBOT] ❌ Critical error loading data:', error);
        // Garantir que o app não quebre
        setContacts([]);
        setCardapio([]);
        setAniversariantes([]);
        setKnowledgeBase([]);
      }
    };

    loadData();
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const newMessage = {
      id: Date.now(),
      text: inputValue,
      isBot: false,
    };

    setMessages((prev) => [...prev, newMessage]);
    const currentInput = inputValue;
    setInputValue('');
    setLoading(true);

    // Simular delay de processamento mais realista
    setTimeout(() => {
      const botResponse = {
        id: Date.now() + 1,
        text: getBotResponse(currentInput),
        isBot: true,
      };
      setMessages((prev) => [...prev, botResponse]);
      setLoading(false);
    }, 800 + Math.random() * 1200); // 0.8-2s delay
  };

  const getBotResponse = (userMessage: string): string => {
    const message = userMessage.toLowerCase().trim();
    
    console.log('[CHATBOT] Processing question:', userMessage);

    // Análise de intenção específica
    const response = analyzeIntentAndRespond(message, userMessage);
    if (response) return response;

    // Busca inteligente na base de conhecimento
    const bestMatch = findBestMatch(message, knowledgeBase);
    if (bestMatch && bestMatch.score > 0.3) { // Threshold mais alto para melhor precisão
      console.log('[CHATBOT] Knowledge match found:', bestMatch.item.id, 'score:', bestMatch.score);
      return bestMatch.item.resposta;
    }

    // Respostas contextuais baseadas em categoria
    if (isAboutContacts(message)) {
      return handleContactQueries(message);
    }

    if (isAboutMenu(message)) {
      return handleMenuQueries(message, userMessage);
    }

    if (isAboutBirthdays(message)) {
      return handleBirthdayQueries(message);
    }

    if (isAboutCompany(message)) {
      return handleCompanyQueries(message);
    }

    // Sugestões inteligentes quando não entende
    return generateIntelligentFallback(message);
  };

  // Análise avançada de intenção
  const analyzeIntentAndRespond = (message: string, originalMessage: string): string | null => {
    // Perguntas sobre pessoas específicas
    if (message.includes('quem é') || message.includes('quem trabalha') || message.includes('cargo')) {
      return handlePersonQueries(message, originalMessage);
    }

    // Perguntas sobre hoje/amanhã/datas específicas
    if (message.includes('hoje') || message.includes('amanhã') || message.includes('ontem')) {
      return handleDateSpecificQueries(message);
    }

    // Perguntas sobre quantidade/estatísticas
    if (message.includes('quantos') || message.includes('quantas')) {
      return handleQuantityQueries(message);
    }

    // Perguntas sobre localização
    if (message.includes('onde') && (message.includes('fica') || message.includes('encontro'))) {
      return handleLocationQueries(message);
    }

    return null;
  };

  // Handler para perguntas sobre pessoas
  const handlePersonQueries = (message: string, originalMessage: string): string => {
    const extractedNames = extractNames(originalMessage);

    if (extractedNames.length > 0) {
      // Busca mais inteligente - prioriza correspondências exatas
      const exactMatches = contacts.filter(c =>
        extractedNames.some(name => c.nome.toLowerCase() === name.toLowerCase())
      );

      const partialMatches = contacts.filter(c =>
        !exactMatches.includes(c) && extractedNames.some(name =>
          c.nome.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(c.nome.toLowerCase().split(' ')[0])
        )
      );

      const foundContacts = [...exactMatches, ...partialMatches];

      if (foundContacts.length > 0) {
        const person = foundContacts[0];
        let response = `📞 **${person.nome}**\n\n`;

        if (person.ramal && person.ramal.trim()) {
          response += `☎️ **Ramal: ${person.ramal}**\n`;
        }
        if (person.telefone && person.telefone.trim()) {
          response += `📱 Telefone: ${person.telefone}\n`;
        }
        if (person.cargo && person.cargo.trim()) {
          response += `💼 Cargo: ${person.cargo}\n`;
        }
        if (person.setor && person.setor.trim()) {
          response += `🏢 Setor: ${person.setor}\n`;
        }
        if (person.localizacao && person.localizacao.trim()) {
          response += `📍 Local: ${person.localizacao}\n`;
        }
        if (person.email && person.email.trim()) {
          response += `📧 Email: ${person.email}\n`;
        }

        if (!person.ramal || !person.ramal.trim()) {
          response += `\n⚠️ Ramal não disponível no cadastro.`;
        }

        if (foundContacts.length > 1) {
          response += `\n\n💡 Encontrei ${foundContacts.length} pessoas. Consulte o Diretório para ver todas.`;
        }

        return response;
      }
    }
    
    // Perguntas sobre relacionamentos organizacionais
    if (message.includes('trabalha com') || message.includes('equipe') || message.includes('time')) {
      const nameMatch = extractNames(message);
      if (nameMatch.length > 0) {
        const person = contacts.find(c => 
          nameMatch.some(name => c.nome.toLowerCase().includes(name.toLowerCase()))
        );
        if (person && person.setor) {
          const colegas = contacts.filter(c => 
            c.setor === person.setor && c.nome !== person.nome
          ).slice(0, 5);
          
          if (colegas.length > 0) {
            const lista = colegas.map(c => `👤 ${c.nome} - ${c.cargo}`).join('\n');
            return `👥 **Equipe do ${person.setor}** (colegas de ${person.nome}):\n\n${lista}${colegas.length === 5 ? '\n\nE outros...' : ''}`;
          }
        }
      }
    }
    
    return 'Não encontrei informações sobre essa pessoa. Tente buscar no Diretório Corporativo ou seja mais específico com o nome completo.';
  };

  // Handler para perguntas sobre datas
  const handleDateSpecificQueries = (message: string): string => {
    const hoje = new Date();
    const amanha = new Date(hoje);
    amanha.setDate(hoje.getDate() + 1);
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);

    if (message.includes('cardápio') || message.includes('almoço') || message.includes('comida')) {
      const formatDate = (date: Date) => date.toLocaleDateString('pt-BR').replace(/\//g, '/');
      
      let targetDate = '';
      let dateLabel = '';
      
      if (message.includes('hoje')) {
        targetDate = formatDate(hoje);
        dateLabel = 'hoje';
      } else if (message.includes('amanhã')) {
        targetDate = formatDate(amanha);
        dateLabel = 'amanhã';
      } else if (message.includes('ontem')) {
        targetDate = formatDate(ontem);
        dateLabel = 'ontem';
      }
      
      if (targetDate) {
        const menuItem = cardapio.find(item => item.data === targetDate);
        if (menuItem) {
          return `🍽️ **Cardápio de ${dateLabel}** (${targetDate}):\n\n🥩 **${menuItem.prato}**\n🥬 **Acompanhamentos**: ${menuItem.acompanhamentos?.join(', ') || 'N/A'}\n🍰 **Sobremesa**: ${menuItem.sobremesa || 'N/A'}`;
        } else {
          return `Não há cardápio disponível para ${dateLabel} (${targetDate}). O cardápio geralmente está disponível apenas para dias úteis.`;
        }
      }
    }

    if (message.includes('aniversário') || message.includes('aniversariante')) {
      const mesAtual = hoje.getMonth() + 1;
      const diaAtual = hoje.getDate();
      
      if (message.includes('hoje')) {
        const aniversariantesHoje = aniversariantes.filter(a => 
          a.mesNumero === mesAtual && a.dia === diaAtual
        );
        
        if (aniversariantesHoje.length > 0) {
          const nomes = aniversariantesHoje.map(a => `🎂 ${a.nome}`).join('\n');
          return `🎉 **Aniversariantes de hoje**:\n\n${nomes}\n\nNão esqueça de parabenizar! 🎈`;
        } else {
          return `Não há aniversariantes hoje (${diaAtual}/${mesAtual}). Consulte o menu 'Aniversariantes' para ver todos do mês.`;
        }
      }
    }

    return `📅 Hoje é ${hoje.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    })}.\n\nPara informações específicas sobre cardápio, aniversários ou agenda, consulte os menus correspondentes na intranet.`;
  };

  // Handler para perguntas sobre quantidade
  const handleQuantityQueries = (message: string): string => {
    if (message.includes('funcionários') || message.includes('colaboradores') || message.includes('pessoas')) {
      return `👥 Temos ${contacts.length} contatos cadastrados no diretório, incluindo representantes e colaboradores da sede em Apucarana-PR e outras localidades.`;
    }

    if (message.includes('salas')) {
      return `🏢 Temos 4 salas disponíveis para reserva:\n• Aquário (8 pessoas)\n• Grande (20 pessoas) \n• Pequena (6 pessoas)\n• Recepção (4 pessoas)\n\nTotal: 38 lugares para reuniões.`;
    }

    if (message.includes('setores') || message.includes('departamentos')) {
      const setores = [...new Set(contacts.map(c => c.setor).filter(Boolean))];
      return `🏭 Temos ${setores.length} setores principais: ${setores.slice(0, 8).join(', ')}${setores.length > 8 ? ' e outros' : ''}.`;
    }

    if (message.includes('cidades') || message.includes('escritórios')) {
      const cidades = [...new Set(contacts
        .map(c => c.localizacao)
        .filter(Boolean)
        .map(loc => loc?.split(' - ')[0])
      )];
      return `📍 Estamos presentes em ${cidades.length} cidades: ${cidades.slice(0, 6).join(', ')}${cidades.length > 6 ? ' e outras' : ''}.`;
    }

    return null;
  };

  // Handler para perguntas sobre localização
  const handleLocationQueries = (message: string): string => {
    if (message.includes('felipe') || message.includes('gerente administrativo')) {
      const felipe = contacts.find(c => c.nome.toLowerCase().includes('felipe') && c.cargo?.includes('Gerente'));
      if (felipe) {
        return `📍 ${felipe.nome} está localizado em ${felipe.localizacao} e pode ser contactado pelo telefone ${felipe.telefone} ou ramal ${felipe.ramal}.`;
      }
    }

    // Busca geral por localização de pessoas
    const nameMatch = extractNames(message);
    if (nameMatch.length > 0) {
      const person = contacts.find(c => 
        nameMatch.some(name => c.nome.toLowerCase().includes(name.toLowerCase()))
      );
      if (person) {
        return `📍 ${person.nome} está localizado em ${person.localizacao || 'localização não especificada'}.`;
      }
    }

    return 'Especifique sobre qual pessoa ou local você gostaria de saber. Posso ajudar a encontrar a localização de qualquer colaborador.';
  };

  // Handlers especializados
  const handleContactQueries = (message: string): string => {
    // Busca específica por nome completo ou parcial
    const extractedNames = extractNames(message);
    if (extractedNames.length > 0) {
      const foundContacts = contacts.filter(c =>
        extractedNames.some(name =>
          c.nome.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(c.nome.toLowerCase().split(' ')[0])
        )
      );

      if (foundContacts.length > 0) {
        if (foundContacts.length === 1) {
          const person = foundContacts[0];
          let response = `📞 **${person.nome}**\n\n`;

          if (person.ramal && person.ramal.trim()) {
            response += `☎️ **Ramal: ${person.ramal}**\n`;
          }
          if (person.telefone && person.telefone.trim()) {
            response += `📱 Telefone: ${person.telefone}\n`;
          }
          if (person.cargo && person.cargo.trim()) {
            response += `💼 Cargo: ${person.cargo}\n`;
          }
          if (person.setor && person.setor.trim()) {
            response += `🏢 Setor: ${person.setor}\n`;
          }
          if (person.localizacao && person.localizacao.trim()) {
            response += `📍 Local: ${person.localizacao}\n`;
          }
          if (person.email && person.email.trim()) {
            response += `📧 Email: ${person.email}\n`;
          }

          if (!person.ramal || !person.ramal.trim()) {
            response += `\n⚠️ Ramal não disponível no cadastro.`;
          }

          return response;
        } else {
          const lista = foundContacts.slice(0, 5).map(p =>
            `📞 **${p.nome}**${p.ramal && p.ramal.trim() ? ` - Ramal: ${p.ramal}` : ' - Sem ramal'}\n   ${p.cargo || 'Colaborador'}${p.setor ? ` (${p.setor})` : ''}`
          ).join('\n\n');

          return `👥 **${foundContacts.length} pessoas encontradas**:\n\n${lista}${foundContacts.length > 5 ? '\n\n💡 Consulte o Diretório para ver todas.' : ''}`;
        }
      }
    }

    // Perguntas sobre hierarquia
    if (message.includes('gerente') || message.includes('coordenador') || message.includes('chefe')) {
      const gerentes = contacts.filter(c => 
        c.cargo?.toLowerCase().includes('gerente') ||
        c.cargo?.toLowerCase().includes('coordenador')
      );
      
      if (gerentes.length > 0) {
        const lista = gerentes.slice(0, 6).map(g => 
          `👔 **${g.nome}** - ${g.cargo} (${g.setor || g.localizacao})`
        ).join('\n');
        
        return `👥 **Liderança da empresa** (${gerentes.length} pessoas):\n\n${lista}${gerentes.length > 6 ? '\n\nVeja todos no Diretório.' : ''}`;
      }
    }

    // Perguntas sobre quem trabalha onde
    if (message.includes('quem trabalha') && (message.includes('em') || message.includes('no'))) {
      const cidades = ['apucarana', 'erechim', 'dourados', 'nova mutum', 'rio verde'];
      const setores = ['rh', 'ti', 't.i', 'comercial', 'financeiro', 'comex', 'contabilidade', 'crédito', 'credito', 'faturamento', 'marketing', 'logística', 'logistica'];
      
      const cidadeMencionada = cidades.find(cidade => message.includes(cidade));
      const setorMencionado = setores.find(setor => message.includes(setor));
      
      if (cidadeMencionada) {
        const pessoasNaCidade = contacts.filter(c => 
          c.localizacao?.toLowerCase().includes(cidadeMencionada)
        );
        
        if (pessoasNaCidade.length > 0) {
          const lista = pessoasNaCidade.slice(0, 8).map(p => 
            `👤 ${p.nome} - ${p.cargo || 'Representante'}${p.setor ? ` (${p.setor})` : ''}`
          ).join('\n');
          
          return `📍 **Equipe em ${cidadeMencionada.charAt(0).toUpperCase() + cidadeMencionada.slice(1)}** (${pessoasNaCidade.length} pessoas):\n\n${lista}${pessoasNaCidade.length > 8 ? '\n\nConsulte o Diretório para ver todos.' : ''}`;
        }
      }
      
      if (setorMencionado) {
        const pessoasNoSetor = contacts.filter(c => {
          const setorLower = c.setor?.toLowerCase() || '';
          const cargoLower = c.cargo?.toLowerCase() || '';

          // Normalizar busca de TI
          if (setorMencionado === 'ti' || setorMencionado === 't.i') {
            return setorLower.includes('ti') || setorLower.includes('t.i') ||
                   cargoLower.includes('ti') || cargoLower.includes('t.i');
          }

          return setorLower.includes(setorMencionado) || cargoLower.includes(setorMencionado);
        });
        
        if (pessoasNoSetor.length > 0) {
          const lista = pessoasNoSetor.slice(0, 6).map(p =>
            `📞 **${p.nome}**${p.ramal && p.ramal.trim() ? ` - Ramal: ${p.ramal}` : ' - Sem ramal'}\n   ${p.cargo}${p.telefone && p.telefone.trim() ? ` | ${p.telefone}` : ''}`
          ).join('\n\n');

          return `🏢 **${setorMencionado.toUpperCase()}** (${pessoasNoSetor.length} pessoas):\n\n${lista}${pessoasNoSetor.length > 6 ? '\n\n💡 Ver todos no Diretório' : ''}`;
        }
      }
    }

    // Perguntas sobre estrutura organizacional
    if (message.includes('organograma') || message.includes('estrutura') || message.includes('hierarquia')) {
      const gerentes = contacts.filter(c => c.cargo?.toLowerCase().includes('gerente')).length;
      const coordenadores = contacts.filter(c => c.cargo?.toLowerCase().includes('coordenador')).length;
      const analistas = contacts.filter(c => c.cargo?.toLowerCase().includes('analista')).length;
      
      return `🏗️ **Estrutura Organizacional**:\n\n👔 ${gerentes} Gerentes\n📊 ${coordenadores} Coordenadores\n📋 ${analistas} Analistas\n👥 ${contacts.length - gerentes - coordenadores - analistas} Outros colaboradores\n\n💡 Acesse o Organograma na seção de Diretório para ver a estrutura visual completa.`;
    }

    // Buscar por setor específico
    const setores = ['rh', 'ti', 't.i', 'comercial', 'financeiro', 'comex', 'contabilidade', 'crédito', 'credito', 'faturamento', 'marketing', 'logística', 'logistica'];
    const setorMencionado = setores.find(setor => message.includes(setor));
    
    if (setorMencionado) {
      const contactsFromSetor = contacts.filter(c => {
        const setorLower = c.setor?.toLowerCase() || '';
        const cargoLower = c.cargo?.toLowerCase() || '';

        // Normalizar busca de TI
        if (setorMencionado === 'ti' || setorMencionado === 't.i') {
          return setorLower.includes('ti') || setorLower.includes('t.i') ||
                 cargoLower.includes('ti') || cargoLower.includes('t.i');
        }

        return setorLower.includes(setorMencionado) || cargoLower.includes(setorMencionado);
      });
      
      if (contactsFromSetor.length > 0) {
        const examples = contactsFromSetor.slice(0, 5).map(c =>
          `📞 **${c.nome}**${c.ramal && c.ramal.trim() ? ` - Ramal: ${c.ramal}` : ' - Sem ramal'}\n   ${c.cargo || 'Colaborador'}${c.telefone && c.telefone.trim() ? ` | ${c.telefone}` : ''}`
        ).join('\n\n');

        return `🏢 **${setorMencionado.toUpperCase()}** (${contactsFromSetor.length} pessoas):\n\n${examples}${contactsFromSetor.length > 5 ? '\n\n💡 Diretório completo: veja todos no menu.' : ''}`;
      }
    }

    // Buscar por cidade específica
    const cidades = ['apucarana', 'erechim', 'dourados', 'nova mutum', 'rio verde'];
    const cidadeMencionada = cidades.find(cidade => message.includes(cidade));
    
    if (cidadeMencionada) {
      const contactsFromCidade = contacts.filter(c => 
        c.localizacao?.toLowerCase().includes(cidadeMencionada)
      );
      
      if (contactsFromCidade.length > 0) {
        const examples = contactsFromCidade.slice(0, 3).map(c => 
          `👤 ${c.nome} - ${c.cargo || 'Representante'}`
        ).join('\n');
        
        return `📍 **Equipe em ${cidadeMencionada}** (${contactsFromCidade.length} pessoas):\n\n${examples}${contactsFromCidade.length > 3 ? '\n\nVeja todos no Diretório.' : ''}`;
      }
    }
    
    return 'Você pode encontrar todos os contatos no "Diretório Corporativo" com filtros por setor e cidade. Temos mais de 150 contatos organizados por localização e departamento.';
  };

  const handleMenuQueries = (message: string, originalMessage: string): string => {
    // Buscar por dia específico
    const dayMatch = originalMessage.match(/(\d{1,2})\/(\d{1,2})/);
    if (dayMatch) {
      const [, day, month] = dayMatch;
      const searchDate = `${day.padStart(2, '0')}/${month.padStart(2, '0')}/2025`;
      
      const menuItem = cardapio.find(item => item.data === searchDate);
      if (menuItem) {
        return `🍽️ **Cardápio de ${searchDate}**:\n\n🥩 **${menuItem.prato}**\n🥬 **Acompanhamentos**: ${menuItem.acompanhamentos?.join(', ') || 'N/A'}\n🍰 **Sobremesa**: ${menuItem.sobremesa || 'N/A'}`;
      } else {
        return `❌ Não encontrei cardápio para ${searchDate}. Talvez seja um final de semana ou feriado?`;
      }
    }

    // Buscar por proteína específica
    if (message.includes('frango') || message.includes('carne') || message.includes('peixe') || message.includes('porco')) {
      const proteinaBuscada = message.includes('frango') ? 'frango' : 
                            message.includes('carne') ? 'carne' : 
                            message.includes('peixe') ? 'peixe' : 'porco';
      
      const diasComProteina = cardapio.filter(item => 
        item.proteina.toLowerCase().includes(proteinaBuscada)
      );
      
      if (diasComProteina.length > 0) {
        const exemplos = diasComProteina.slice(0, 3).map(item =>
          `📅 ${item.data}: ${item.prato}`
        ).join('\n');
        
        return `🥩 **Dias com ${proteinaBuscada}** (${diasComProteina.length} dias encontrados):\n\n${exemplos}${diasComProteina.length > 3 ? '\n\n💡 Consulte o Cardápio completo para ver todos.' : ''}`;
      }
    }

    return 'Consulte o menu "Cardápio" para ver o cardápio completo do mês com todos os pratos, proteínas, acompanhamentos e sobremesas.';
  };

  const handleBirthdayQueries = (message: string): string => {
    const hoje = new Date();
    const mesAtual = hoje.getMonth() + 1;
    const diaAtual = hoje.getDate();

    if (message.includes('hoje')) {
      const aniversariantesHoje = aniversariantes.filter(a => 
        a.mesNumero === mesAtual && a.dia === diaAtual
      );
      
      if (aniversariantesHoje.length > 0) {
        const nomes = aniversariantesHoje.map(a => `🎂 ${a.nome}`).join('\n');
        return `🎉 **Aniversariantes de hoje**:\n\n${nomes}\n\nNão esqueça de parabenizar! 🎈`;
      } else {
        return `Não há aniversariantes hoje (${diaAtual}/${mesAtual}). 🗓️`;
      }
    }

    if (message.includes('próximos') || message.includes('proximos')) {
      const proximosAniversariantes = aniversariantes
        .filter(a => a.mesNumero === mesAtual && a.dia > diaAtual)
        .sort((a, b) => a.dia - b.dia)
        .slice(0, 5);
      
      if (proximosAniversariantes.length > 0) {
        const lista = proximosAniversariantes.map(a => 
          `🎂 ${a.nome} - ${a.dia}/${a.mesNumero}`
        ).join('\n');
        return `🗓️ **Próximos aniversariantes**:\n\n${lista}`;
      }
    }

    const aniversariantesDoMes = aniversariantes.filter(a => a.mesNumero === mesAtual);
    return `🎂 Este mês temos ${aniversariantesDoMes.length} aniversariantes. Acesse o menu 'Aniversariantes' para ver todos com datas e idades.`;
  };

  const handleCompanyQueries = (message: string): string => {
    if (message.includes('sede') || message.includes('matriz')) {
      return '🏢 Nossa sede fica em **Apucarana - PR**. Lá temos as equipes de Comercial, Comex, Contabilidade, Crédito, Faturamento, Financeiro, RH e TI.';
    }

    if (message.includes('história') || message.includes('fundação')) {
      return '📚 O Grupo Cropfield é uma empresa consolidada no agronegócio brasileiro, com forte presença em tecnologia agrícola e representação comercial. Para mais detalhes históricos, consulte o setor de RH.';
    }

    if (message.includes('missão') || message.includes('valores')) {
      return '🎯 Nossa missão é fornecer soluções inovadoras para o agronegócio brasileiro. Para informações detalhadas sobre missão, visão e valores, consulte o material institucional disponível com o RH.';
    }

    return null;
  };

  // Função melhorada para encontrar correspondências
  const findBestMatch = (userMessage: string, knowledge: KnowledgeItem[]) => {
    let bestMatch = { item: null as KnowledgeItem | null, score: 0 };
    
    for (const item of knowledge) {
      let score = 0;
      
      // Verificar correspondências nas palavras-chave (peso maior)
      for (const keyword of item.palavras_chave) {
        if (userMessage.includes(keyword.toLowerCase())) {
          score += 2; // Peso maior para palavras-chave
        }
      }
      
      // Verificar correspondências na pergunta (peso menor)
      const perguntaWords = item.pergunta.toLowerCase().split(' ');
      for (const word of perguntaWords) {
        if (word.length > 3 && userMessage.includes(word)) {
          score += 0.5;
        }
      }
      
      // Bonus para correspondências múltiplas
      const keywordsFound = item.palavras_chave.filter(keyword => 
        userMessage.includes(keyword.toLowerCase())
      );
      
      if (keywordsFound.length > 1) {
        score += keywordsFound.length * 0.5;
      }
      
      // Bonus para correspondência de categoria
      if (userMessage.includes(item.categoria)) {
        score += 1;
      }
      
      if (score > bestMatch.score) {
        bestMatch = { item, score };
      }
    }
    
    return bestMatch;
  };

  // Funções auxiliares para categorização
  const isAboutContacts = (message: string): boolean => {
    return message.includes('contato') || message.includes('telefone') || 
           message.includes('ramal') || message.includes('email') ||
           message.includes('quem trabalha') || message.includes('setor');
  };

  const isAboutMenu = (message: string): boolean => {
    return message.includes('cardápio') || message.includes('almoço') || 
           message.includes('comida') || message.includes('prato') ||
           message.includes('proteína') || message.includes('menu');
  };

  const isAboutBirthdays = (message: string): boolean => {
    return message.includes('aniversário') || message.includes('aniversariante') ||
           message.includes('birthday') || message.includes('nascimento');
  };

  const isAboutCompany = (message: string): boolean => {
    return message.includes('empresa') || message.includes('cropfield') || 
           message.includes('grupo') || message.includes('negócio') ||
           message.includes('história') || message.includes('sobre');
  };

  // Função para extrair nomes da mensagem
  const extractNames = (message: string): string[] => {
    const words = message.toLowerCase().split(/\s+/);
    const names: string[] = [];
    
    // Buscar após indicadores de pessoa
    const indicators = ['é', 'trabalha', 'está', 'cargo', 'contato', 'quem', 'onde', 'como'];
    
    for (let i = 0; i < words.length; i++) {
      if (indicators.includes(words[i]) && i < words.length - 1) {
        // Capturar próximas 1-3 palavras como possível nome
        const possibleName = words.slice(i + 1, i + 4).join(' ');
        if (possibleName.length > 2) {
          names.push(possibleName);
        }
      }
    }
    
    // Buscar nomes próprios (palavras capitalizadas no texto original)
    const originalWords = message.split(' ');
    const capitalizedWords = originalWords.filter(word => 
      word.length > 2 && word[0] === word[0].toUpperCase() && 
      !['Como', 'Onde', 'Qual', 'Quem', 'Quando', 'Por', 'Que', 'Se', 'Ele', 'Ela'].includes(word)
    );
    
    if (capitalizedWords.length > 0) {
      names.push(...capitalizedWords.map(w => w.toLowerCase()));
    }
    
    // Buscar padrões comuns de nomes brasileiros
    const nomePattern = /\b([A-Z][a-z]+)\s+([A-Z][a-z]+)\b/g;
    let match;
    while ((match = nomePattern.exec(message)) !== null) {
      names.push(`${match[1].toLowerCase()} ${match[2].toLowerCase()}`);
    }
    
    return [...new Set(names)]; // Remove duplicatas
  };

  // Fallback inteligente
  const generateIntelligentFallback = (message: string): string => {
    const suggestions = [];
    
    if (message.includes('problema') || message.includes('erro')) {
      suggestions.push('🔧 "Como entro em contato com o TI?"');
      suggestions.push('💻 "Como solicito equipamentos?"');
    }
    
    if (message.includes('pessoa') || message.includes('funcionário')) {
      suggestions.push('👤 "Quem é [nome da pessoa]?"');
      suggestions.push('📞 "Contatos do setor [nome do setor]"');
    }
    
    if (message.includes('data') || message.includes('quando')) {
      suggestions.push('📅 "Qual o cardápio de hoje?"');
      suggestions.push('🎂 "Quem faz aniversário hoje?"');
    }

    const baseSuggestions = [
      '🏢 "Qual o horário da empresa?"',
      '📞 "Contatos do RH"',
      '🍽️ "Cardápio de hoje"',
      '📅 "Como reservo uma sala?"',
      '🎯 "Como funciona a pontuação?"'
    ];

    const allSuggestions = suggestions.length > 0 ? suggestions : baseSuggestions;
    
    return `🤔 Não encontrei uma resposta específica, mas posso ajudar com:\n\n${allSuggestions.slice(0, 4).join('\n')}\n\n💡 **Dica**: Seja específico! Pergunte sobre pessoas, datas, setores ou funcionalidades da intranet.`;
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="relative p-2 hover:bg-surface-container rounded-full transition-all group"
        title="Assistente Virtual Cropfield"
      >
        <MessageCircle className="w-5 h-5 text-secondary group-hover:text-primary transition-colors" />
        <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-primary rounded-full animate-pulse border-2 border-white"></span>
      </button>

      {isOpen && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg h-[85vh] sm:h-[600px] max-h-[700px] flex flex-col border border-outline-variant">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-primary text-white rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">Assistente Cropfield</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                    <p className="text-xs opacity-90">Online - {contacts.length}+ contatos</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-surface-container-low">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                >
                  <div className={`flex items-start space-x-3 max-w-[90%] ${message.isBot ? '' : 'flex-row-reverse space-x-reverse'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.isBot ? 'bg-primary' : 'bg-secondary'
                    }`}>
                      {message.isBot ? <Bot className="w-4 h-4 text-white" /> : <User className="w-4 h-4 text-white" />}
                    </div>
                    <div
                      className={`px-4 py-3 rounded-2xl ${
                        message.isBot
                          ? 'bg-white dark:bg-gray-700 text-on-surface shadow-sm border border-outline-variant'
                          : 'bg-primary text-white shadow-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.text}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="flex items-start space-x-3 max-w-[90%]">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white dark:bg-gray-700 px-4 py-3 rounded-2xl shadow-sm border border-outline-variant">
                      <div className="flex items-center space-x-1">
                        <div className="text-sm text-secondary mr-2">Analisando</div>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-outline-variant bg-white dark:bg-gray-800 sm:rounded-b-2xl">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !loading && handleSendMessage()}
                  placeholder="Pergunte sobre pessoas, cardapio, reservas..."
                  disabled={loading}
                  className="flex-1 px-4 py-2 border border-outline-variant rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm bg-surface-container-low focus:bg-white transition-colors"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={loading || !inputValue.trim()}
                  className="px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>

              {/* Sugestoes */}
              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  'Contatos do RH',
                  'Contatos do TI',
                  'Cardapio de hoje',
                  'Como agendo uma sala?',
                  'Aniversariantes hoje'
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setInputValue(suggestion)}
                    className="px-3 py-1 text-xs bg-surface-container text-secondary rounded-full hover:bg-primary/10 hover:text-primary transition-all border border-outline-variant"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};